// 2026-02 — Break Insights helpers. Single home for all break math + display.
// Handles BOTH break shapes that the backend may emit:
//   - v2 `clock_sessions.breaks`: [{start, end}]
//   - v1 `clock_entries.breaks`:  [{break_in, break_out}]
// The `/api/attendance/{ws}/report` response normalises to `{start, end, minutes}`
// (top-level numeric `break_minutes` per day), but the live
// `/api/attendance/status` payload returns the raw v2 shape — both work.

// Normalise either shape → `{start, end}` (both ISO strings, end may be null
// if the break is still in progress).
export function normalizeBreaks(breaks = []) {
  if (!Array.isArray(breaks)) return [];
  return breaks
    .filter((b) => b && typeof b === 'object')
    .map((b) => ({
      start: b.start || b.break_in || null,
      end: b.end || b.break_out || null,
    }))
    .filter((b) => !!b.start);
}

// Total break minutes. If a break is still open (end == null), uses `now`
// as the live end so the on-break counter ticks up in real time.
export function calcBreakMinutes(breaks = [], now = new Date()) {
  const list = normalizeBreaks(breaks);
  let total = 0;
  for (const b of list) {
    try {
      const start = new Date(b.start);
      const end = b.end ? new Date(b.end) : now;
      const ms = end.getTime() - start.getTime();
      if (Number.isFinite(ms) && ms > 0) total += ms / 60000;
    } catch (_e) { /* ignore parse errors */ }
  }
  return total;
}

// Minutes for the SINGLE open break (or 0). Used by the live "On break · X"
// counter so we measure the current break only, not historical breaks.
export function calcOpenBreakMinutes(breaks = [], now = new Date()) {
  const list = normalizeBreaks(breaks);
  const open = list.find((b) => !b.end);
  if (!open) return 0;
  try {
    const ms = now.getTime() - new Date(open.start).getTime();
    return Math.max(0, ms / 60000);
  } catch (_e) {
    return 0;
  }
}

// Open break seconds — gives the topbar a true stopwatch feel.
export function calcOpenBreakSeconds(breaks = [], now = new Date()) {
  return Math.round(calcOpenBreakMinutes(breaks, now) * 60);
}

// "12 min" / "1h 25m" / "12s" (under-a-minute uses seconds for live counter).
export function formatDuration(minutes, opts = {}) {
  const { allowSeconds = false } = opts;
  if (!Number.isFinite(minutes) || minutes < 0) return '0 min';
  if (minutes < 1) {
    if (allowSeconds) {
      const sec = Math.max(0, Math.round(minutes * 60));
      return `${sec}s`;
    }
    return '< 1 min';
  }
  const totalMin = Math.round(minutes);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// Stopwatch format mm:ss for the live counter (used when allowSeconds=true).
export function formatStopwatch(totalSeconds) {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00';
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Local time-of-day "1:15 PM" — browser-local per user spec (option a).
export function formatTimeLocal(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (_e) {
    return '—';
  }
}

// One-line segment label: "1:15 PM – 1:35 PM · 20 min" (or "1:15 PM – ongoing"
// when end is null).
export function formatBreakSegment(b, idx = 0) {
  const start = formatTimeLocal(b.start);
  const end = b.end ? formatTimeLocal(b.end) : 'ongoing';
  let mins = 0;
  if (b.end) {
    mins = (new Date(b.end).getTime() - new Date(b.start).getTime()) / 60000;
  } else {
    mins = (Date.now() - new Date(b.start).getTime()) / 60000;
  }
  const label = `Break ${idx + 1}`;
  return `${label}: ${start} – ${end} · ${formatDuration(mins)}`;
}

// Worked minutes from clock-in to (clock-out OR now), minus total break time.
export function calcWorkedMinutes(clockInIso, clockOutIso = null, breaks = [], now = new Date()) {
  if (!clockInIso) return 0;
  try {
    const start = new Date(clockInIso);
    const end = clockOutIso ? new Date(clockOutIso) : now;
    const totalMin = (end.getTime() - start.getTime()) / 60000;
    const brk = calcBreakMinutes(breaks, now);
    return Math.max(0, totalMin - brk);
  } catch (_e) {
    return 0;
  }
}
